import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Phone } from "lucide-react";
import { useLanguage } from "../i18n";
import { gelatoFlavors, IMAGES, CONTACTS } from "../data/menu";
import { Reveal } from "../components/Reveal";
import { Footer } from "../components/Footer";
import { Marquee } from "../components/Marquee";

const tagColor = {
  classico: "border-ink/30 text-ink/70",
  crema: "border-gold text-gold",
  frutta: "border-terra/70 text-terra",
  "della casa": "border-ink text-ink",
};

export default function Gelateria() {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="gelateria-page">
      <section className="relative pt-40 pb-24 md:pt-52 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.gelato} alt="Gelato artigianale" className="h-full w-full object-cover opacity-20" data-testid="gelateria-hero-image" />
          <div className="absolute inset-0 bg-gradient-to-b from-paper/75 via-paper/90 to-paper" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/" data-testid="gelateria-back-link" className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-ink/60 hover:text-terra transition-colors duration-300 mb-10">
            <ArrowLeft size={14} /> Da Polin
          </Link>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-xs tracking-[0.35em] uppercase text-terra"
            data-testid="gelateria-kicker"
          >
            {t("gelato_page_kicker")}
          </motion.p>
          <h1 className="font-serif text-ink leading-[0.95] text-5xl md:text-7xl lg:text-8xl mt-6 max-w-4xl">
            <span className="block overflow-hidden">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
                {t("gelato_page_title")}
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-2xl text-base md:text-lg text-ink/70 font-light leading-relaxed"
            data-testid="gelateria-intro"
          >
            {t("gelato_page_text")}
          </motion.p>
        </div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-6xl text-ink" data-testid="gelateria-flavors-title">{t("gelato_flavors_title")}</h2>
          <div className="mt-6 flex flex-wrap gap-5 text-xs tracking-[0.2em] uppercase">
            {["classico", "crema", "frutta", "della casa"].map((tag) => (
              <span key={tag} className={`inline-flex items-center gap-2 ${tagColor[tag]}`} data-testid={`legend-${tag.replace(" ", "-")}`}>
                <span className={`inline-block w-2 h-2 rounded-full border ${tagColor[tag]}`} /> {t(`tag_${tag}`)}
              </span>
            ))}
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {gelatoFlavors.map((f, i) => (
            <Reveal key={f.name} delay={Math.min(i * 0.04, 0.4)}>
              <div
                data-testid={`flavor-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group rounded-xl border border-line bg-surface px-6 py-8 hover:border-terra/50 hover:bg-terra/5 transition-colors duration-500"
              >
                <span className={`inline-block w-2 h-2 rounded-full border mb-4 ${tagColor[f.tag]}`} />
                <p className="font-serif text-xl md:text-2xl text-ink group-hover:text-terra transition-colors duration-300">{f.name}</p>
                <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-muted2">{t(`tag_${f.tag}`)}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-16">
          <a
            href={CONTACTS.phoneHref}
            data-testid="gelateria-call-button"
            className="inline-flex items-center gap-3 rounded-full bg-terra px-8 py-4 text-xs tracking-[0.22em] uppercase text-cream font-semibold hover:bg-ink transition-colors duration-300"
          >
            <Phone size={14} /> {t("gelato_cta")} — {CONTACTS.phone}
          </a>
        </Reveal>
      </section>

      <Footer />
    </main>
  );
}
